import React, { useEffect, useRef, useState } from 'react';
import { useOwned } from './useCrm';
import { useNavigate } from 'react-router-dom';
import { saveContactChecked, enrollContacts, watchPath, saveRecord, logActivity, noCalls, optedOutTexts, phoneE164, isPaused } from '../../services/crmService';
import { NOTIFICATIONS_ENABLED } from '../../config/notifications';
import { ui, MUTED } from './crmUi';
import { applyMerge, insertAtCursor, InsertFieldMenu, SAMPLE_CONTACT } from './merge';
import { canMessageLead } from '../../shared/leadPermissions';
import { planBulkHashtag, planBulkEnroll } from '../../services/bulkLeadActions';

// Sticky bar for actions on several selected contacts. Sending isn't connected yet, so Send Email /
// Send Text only log what would have gone out. Opted-out / DNC contacts are skipped automatically.
const overlay = { position: 'fixed', inset: 0, background: 'rgba(20, 24, 33, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 };
const modal = { background: '#FFFFFF', borderRadius: 12, padding: 24, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.25)', boxSizing: 'border-box' };

const canEmail = (c) => !!c.email && !c.email_opt_out;
const canText = (c) => !!phoneE164(c.phone) && !optedOutTexts(c);
const canCall = (c) => !!phoneE164(c.phone) && !noCalls(c) && !isPaused(c);

export default function BulkBar({ selected, uid, isAdmin = false, onClear, initialStatus = '', agent = {} }) {
  const textRef = useRef(null);
  const navigate = useNavigate();
  const [mode, setMode] = useState(null); // 'email' | 'text' | 'list'
  const [templates, setTemplates] = useState([]);
  const [lists, setLists] = useState([]);
  const [tplId, setTplId] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [listId, setListId] = useState('');
  const [newList, setNewList] = useState('');
  const [status, setStatus] = useState(initialStatus);
  const [busy, setBusy] = useState(false);
  const own_Templates = useOwned('crm/templates', { scoped: true });
  useEffect(() => setTemplates(own_Templates), [own_Templates]);
  const own_Lists = useOwned('crm/lists');
  useEffect(() => setLists(own_Lists), [own_Lists]);

  const [tagText, setTagText] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const own_Campaigns = useOwned('crm/campaigns', { scoped: ['team'] });
  const own_Enrollments = useOwned('crm/enrollments');
  const tagPlan = mode === 'tag' ? planBulkHashtag(selected, tagText, uid, isAdmin) : null;
  const pickedCampaign = (own_Campaigns || []).find((c) => c.id === campaignId) || null;
  const enrollPlan = mode === 'campaign' && pickedCampaign ? planBulkEnroll(pickedCampaign, selected, uid, isAdmin, own_Enrollments || []) : null;
  // Adds the hashtag to every selected lead you can see. Hashtag campaigns may enroll (only leads you may email);
  // nothing is sent while sending is off.
  const applyTag = async () => {
    if (!tagPlan || !tagPlan.tag) { setStatus('Type a hashtag first.'); return; }
    setBusy(true);
    let done = 0; let failed = 0;
    for (const u of tagPlan.updates) { try { await saveContactChecked(u.contact, { tags: u.tags }, uid, isAdmin); done += 1; } catch (e) { failed += 1; } }
    setBusy(false);
    setStatus(`Added ${tagPlan.tag} to ${done} lead(s).${tagPlan.already ? ` ${tagPlan.already} already had it.` : ''}${skippedText(tagPlan.skipped + failed, 'not yours to edit')}`);
    close(); setTagText('');
  };
  const applyCampaign = async () => {
    if (!enrollPlan) { setStatus('Pick a campaign first.'); return; }
    setBusy(true);
    try {
      await enrollContacts(pickedCampaign, enrollPlan.go, uid, { isAdmin });
      setStatus(`Enrolled ${enrollPlan.go.length} lead(s) in "${pickedCampaign.name}".${enrollPlan.skipped ? ` Skipped ${enrollPlan.skipped} (${[enrollPlan.why.notAllowed && `${enrollPlan.why.notAllowed} not assigned to you or entered by you`, enrollPlan.why.notEligible && `${enrollPlan.why.notEligible} not eligible for this campaign`, enrollPlan.why.already && `${enrollPlan.why.already} already in it`].filter(Boolean).join(', ')}).` : ''}${NOTIFICATIONS_ENABLED ? '' : ' Nothing is sent while sending is off.'}`);
    } catch (e) { setStatus(`Could not enroll: ${e.message || 'error'}.`); }
    setBusy(false); close(); setCampaignId('');
  };
  const split = (ok) => ({ go: selected.filter(ok), skipped: selected.filter((c) => !ok(c)).length });
  const close = () => { setMode(null); setTplId(''); setSubject(''); setMessage(''); setListId(''); setNewList(''); };
  const skippedText = (n, why) => (n ? ` ${n} skipped (${why}).` : '');

  const sendEmail = async () => {
    const { go, skipped } = split((c) => canEmail(c) && canMessageLead(c, uid, isAdmin));
    const t = templates.find((x) => x.id === tplId);
    const subj = t ? t.subject || t.name : subject;
    setBusy(true);
    try {
      await Promise.all(go.map((c) => logActivity(c.id, { type: 'email', bulk: true, templateId: tplId || null, subject: subj, sent: NOTIFICATIONS_ENABLED, text: `Bulk email${NOTIFICATIONS_ENABLED ? '' : ' (not sent: email sending is off)'}: ${subj || '(no subject)'}` }, uid)));
      setStatus(`Email logged for ${go.length}.${skippedText(skipped, 'opted out or no email')}`); close();
    } catch (err) { setStatus('Could not log the email. Please try again.'); }
    setBusy(false);
  };
  const sendText = async () => {
    const { go, skipped } = split((c) => canText(c) && canMessageLead(c, uid, isAdmin));
    setBusy(true);
    try {
      await Promise.all(go.map((c) => logActivity(c.id, { type: 'text', bulk: true, sent: NOTIFICATIONS_ENABLED, notes: applyMerge(message.trim(), c, agent), text: `Bulk text${NOTIFICATIONS_ENABLED ? '' : ' (not sent: texting is off)'}: ${applyMerge(message.trim(), c, agent)}` }, uid)));
      setStatus(`Text logged for ${go.length}.${skippedText(skipped, 'opted out or no phone')}`); close();
    } catch (err) { setStatus('Could not log the text. Please try again.'); }
    setBusy(false);
  };
  const addToList = async () => {
    const ids = selected.map((c) => c.id);
    setBusy(true);
    try {
      if (newList.trim()) {
        await saveRecord('crm/lists', null, { name: newList.trim(), kind: 'static', contactIds: ids, tags: [], matchAll: false }, uid);
        setStatus(`Created list "${newList.trim()}" with ${ids.length}.`);
      } else {
        const l = lists.find((x) => x.id === listId);
        if (!l) { setBusy(false); return; }
        if (l.kind === 'smart') { setStatus('That is a smart (tag) list. Add a tag instead, or pick a regular list.'); setBusy(false); return; }
        const merged = [...new Set([...(l.contactIds || []), ...ids])];
        await saveRecord('crm/lists', l.id, { contactIds: merged }, uid);
        setStatus(`Added ${merged.length - (l.contactIds || []).length} to "${l.name}".`);
      }
      close();
    } catch (err) { setStatus('Could not update the list. Please try again.'); }
    setBusy(false);
  };
  const toDialer = async () => {
    const { go, skipped } = split(canCall);
    if (!go.length) { setStatus(`Nobody to call.${skippedText(skipped, 'DNC, opted out, paused, or no phone')}`); return; }
    setBusy(true);
    try {
      const name = `Dialer session ${new Date().toLocaleString()}`;
      const id = await saveRecord('crm/lists', null, { name, kind: 'static', contactIds: go.map((c) => c.id), tags: [], matchAll: false, dialerSession: true }, uid);
      navigate(`/leads/dialer?list=${id}`);
    } catch (err) { setStatus('Could not start the dialer session.'); setBusy(false); }
  };

  if (!selected.length && !status) return null;
  const preview = mode === 'email' ? split((c) => canEmail(c) && canMessageLead(c, uid, isAdmin)) : mode === 'text' ? split((c) => canText(c) && canMessageLead(c, uid, isAdmin)) : null;

  return (
    <>
      <div style={{ position: 'sticky', top: 0, zIndex: 20, background: '#1a1a1a', color: '#FFFFFF', borderBottom: '2px solid #D4AF37', borderRadius: 8, padding: '10px 14px', marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        {selected.length > 0 && <>
          <strong style={{ marginRight: 6 }}>{selected.length} selected</strong>
          <button style={ui.btn} disabled={busy} onClick={() => navigate('/leads/email-builder', { state: { recipients: selected.filter((c) => canMessageLead(c, uid, isAdmin)).map((c) => c.id) } })}>Send Email</button>
          <button style={ui.btn} disabled={busy} onClick={() => { setStatus(''); setMode('text'); }}>Send Text</button>
          <button style={ui.btn} disabled={busy} onClick={() => { setStatus(''); setMode('list'); }}>Add to List</button>
          <button style={ui.btn} disabled={busy} onClick={toDialer}>Add to Power Dialer</button>
          <button style={ui.btn} disabled={busy} onClick={() => { setStatus(''); setMode('tag'); }}>Add hashtag</button>
          <button style={ui.btn} disabled={busy} onClick={() => { setStatus(''); setMode('campaign'); }}>Add to campaign</button>
          <button style={{ ...ui.ghost, background: 'transparent', color: '#FFFFFF' }} onClick={() => { setStatus(''); onClear(); }}>Clear</button>
        </>}
        {status && <span style={{ fontSize: 13, color: '#f6e7b4', flexBasis: '100%' }}>{status} {!selected.length && <button style={{ background: 'none', border: 0, color: '#FFFFFF', textDecoration: 'underline', cursor: 'pointer' }} onClick={() => setStatus('')}>Dismiss</button>}</span>}
      </div>

      {mode && (
        <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
          <div style={modal} role="dialog" aria-modal="true">
            {mode === 'email' && (<>
              <h3 style={ui.sectionTitle}>Email {preview.go.length} contacts</h3>
              {preview.skipped > 0 && <p style={ui.sub}>{preview.skipped} will be skipped (opted out or no email).</p>}
              <label style={ui.label}>Template</label>
              <select style={ui.input} value={tplId} onChange={(e) => setTplId(e.target.value)}>
                <option value="">No template (subject only)</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              {!tplId && (<><label style={ui.label}>Subject</label><input style={ui.input} value={subject} onChange={(e) => setSubject(e.target.value)} /></>)}
            </>)}
            {mode === 'tag' && (<>
              <h3 style={ui.sectionTitle}>Add a hashtag to {selected.length} lead(s)</h3>
              <label style={ui.label}>Hashtag</label>
              <input style={ui.input} placeholder="#Kingston" value={tagText} onChange={(e) => setTagText(e.target.value)} aria-label="Hashtag" />
              {tagPlan && tagPlan.tag && <p style={ui.sub}>{tagPlan.updates.length} will get {tagPlan.tag}.{tagPlan.already ? ` ${tagPlan.already} already have it.` : ''}{tagPlan.skipped ? ` ${tagPlan.skipped} skipped.` : ''}</p>}
            </>)}
            {mode === 'campaign' && (<>
              <h3 style={ui.sectionTitle}>Add {selected.length} lead(s) to a campaign</h3>
              <label style={ui.label}>Campaign</label>
              <select style={ui.input} value={campaignId} onChange={(e) => setCampaignId(e.target.value)} aria-label="Campaign">
                <option value="">Pick a campaign</option>
                {(own_Campaigns || []).map((c) => <option key={c.id} value={c.id}>{c.name || 'Untitled'}{c.active ? '' : ' (inactive)'}</option>)}
              </select>
              {enrollPlan && <p style={ui.sub}>{enrollPlan.go.length} will be enrolled.{enrollPlan.skipped ? ` ${enrollPlan.skipped} skipped (only leads assigned to you or entered by you, eligible and not already in it).` : ''}</p>}
              {!NOTIFICATIONS_ENABLED && <p style={{ fontSize: 12, color: MUTED }}>Sending is off: enrolling only schedules steps and tasks; nothing is sent.</p>}
            </>)}
            {mode === 'text' && (<>
              <h3 style={ui.sectionTitle}>Text {preview.go.length} contacts</h3>
              {preview.skipped > 0 && <p style={ui.sub}>{preview.skipped} will be skipped (opted out or no phone).</p>}
              <label style={ui.label}>Message</label>
              <textarea ref={textRef} style={{ ...ui.input, minHeight: 100 }} value={message} onChange={(e) => setMessage(e.target.value)} />
              <div style={{ ...ui.row, marginTop: 6 }}>
                <InsertFieldMenu style={{ ...ui.input, width: 'auto', marginTop: 0 }} onInsert={(tok) => setMessage((m) => insertAtCursor(textRef.current, m, tok))} />
                <span style={{ fontSize: 12, color: MUTED }}>{message.length} characters</span>
              </div>
              {message.includes('{{') && <div style={{ fontSize: 12, color: MUTED, marginTop: 6, whiteSpace: 'pre-wrap' }}>Preview: {applyMerge(message, SAMPLE_CONTACT, agent)}</div>}
            </>)}
            {mode === 'list' && (<>
              <h3 style={ui.sectionTitle}>Add {selected.length} to a list</h3>
              <label style={ui.label}>Existing list</label>
              <select style={ui.input} value={listId} onChange={(e) => { setListId(e.target.value); setNewList(''); }}>
                <option value="">Choose a list</option>
                {lists.filter((l) => l.kind !== 'smart' && !l.dialerSession).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              <label style={ui.label}>Or create a new list</label>
              <input style={ui.input} placeholder="New list name" value={newList} onChange={(e) => { setNewList(e.target.value); setListId(''); }} />
            </>)}
            {(mode === 'email' || mode === 'text') && !NOTIFICATIONS_ENABLED && <div style={{ ...ui.notice, marginTop: 10 }}>Sending isn't turned on yet. This logs the {mode} on each contact's timeline; nothing goes out.</div>}
            <div style={{ ...ui.row, marginTop: 14 }}>
              {mode === 'email' && <button style={ui.btn} disabled={busy || !preview.go.length || (!tplId && !subject.trim())} onClick={sendEmail}>{NOTIFICATIONS_ENABLED ? 'Send' : 'Log email'}</button>}
              {mode === 'text' && <button style={ui.btn} disabled={busy || !preview.go.length || !message.trim()} onClick={sendText}>{NOTIFICATIONS_ENABLED ? 'Send' : 'Log text'}</button>}
              {mode === 'list' && <button style={ui.btn} disabled={busy || (!listId && !newList.trim())} onClick={addToList}>Add</button>}
              {mode === 'tag' && <button style={ui.btn} disabled={busy || !tagPlan?.tag || !tagPlan.updates.length} onClick={applyTag}>Add hashtag</button>}
              {mode === 'campaign' && <button style={ui.btn} disabled={busy || !enrollPlan || !enrollPlan.go.length} onClick={applyCampaign}>Enroll</button>}
              <button style={ui.ghost} onClick={close}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
